import {
  ApiError,
  attributePriceFor,
  formatMoney,
  type InventoryCost,
  identifierRejectionReason,
  isUnitTracked,
  PROVISIONING_PIPELINE,
  type ProductAttributePrice,
  queryKeys,
  UNIT_IDENTIFIER_LABELS,
  type Unit,
  type UnitFilterAnswer,
  type UnitStatus,
  unitIdentifierKind,
  VENDOR_PAYMENT_STATUSES,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  UNIT_STATUS_TONE,
  useToast,
  VENDOR_PAYMENT_STATUS_TONE,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import JsBarcode from 'jsbarcode'
import {
  Boxes,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  DollarSign,
  History,
  ListChecks,
  MoreVertical,
  PackageX,
  Pencil,
  Printer,
  Search,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { BulkActionBar, InfiniteScrollFooter } from '../components/data-table.js'
import { FitPartForm, fittedPartSummary, RemoveCostDialog } from '../components/fitted-parts.js'
import { UnitAttributeBadges } from '../components/unit-attributes.js'
import { StartReturnDialog } from '../components/vendor-returns.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { LABEL_PRESETS, type LabelPreset, printLabelSheet } from '../lib/label-print.js'

/** The next stage in the chain, for the one-click advance button. */
const NEXT_STAGE: Partial<Record<UnitStatus, UnitStatus>> = {
  received: 'provisioning',
  provisioning: 'sellable',
  verified: 'sellable',
}

/** The previous stage, for the send-back button in the bulk action bar —
 *  only the backward moves UNIT_TRANSITIONS actually legalizes. */
const PREV_STAGE: Partial<Record<UnitStatus, UnitStatus>> = {
  provisioning: 'received',
  verified: 'provisioning',
  sellable: 'provisioning',
}

/** What happens to a device at each stage, shown under the funnel cards. */
const STAGE_CAPTION: Record<UnitStatus, string> = {
  received: 'Arrived from the vendor, not yet touched.',
  provisioning: 'Filter being installed and locked.',
  verified: 'No longer used — retired step.',
  sellable: 'Provisioned — ready to sell.',
  reserved: 'Attached to an open order.',
  sold: 'Sold to a customer.',
  returned: 'Came back from a customer; needs re-provisioning.',
  rma: 'Sent back to the vendor.',
  scrapped: 'Written off.',
  rented: 'Out with a customer on a rental.',
  lost: 'Never came back from a rental.',
}

function daysSince(iso: string): number {
  // Compare calendar dates in the viewer's timezone, not raw elapsed
  // milliseconds — the server clock stamps `iso`, the browser clock reads
  // it, and any skew between them must never show as a negative age.
  const then = new Date(iso)
  const now = new Date()
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime()
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.max(0, Math.round((nowDay - thenDay) / 86_400_000))
}

interface UnitProductGroup {
  productId: string
  productName: string
  units: Unit[]
}

/** One row per product within the current stage/page, in server order. */
function groupUnitsByProduct(units: Unit[]): UnitProductGroup[] {
  const groups = new Map<string, UnitProductGroup>()
  for (const unit of units) {
    const group = groups.get(unit.productId)
    if (group) group.units.push(unit)
    else
      groups.set(unit.productId, {
        productId: unit.productId,
        productName: unit.productName,
        units: [unit],
      })
  }
  return [...groups.values()]
}

function ageBadge(iso: string) {
  const days = daysSince(iso)
  // Ageing colour: a phone sitting in a stage for a week is a problem
  // someone should see without running a report.
  const tone = days >= 7 ? 'danger' : days >= 3 ? 'warning' : 'neutral'
  return <Badge tone={tone}>{days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`}</Badge>
}

function locationSummary(units: Unit[]): string {
  const names = [...new Set(units.map((u) => u.locationName ?? '—'))]
  return names.length > 1 ? `${names.length} locations` : (names[0] ?? '—')
}

/** Native checkboxes have no `indeterminate` prop — only a DOM property. */
function TriStateCheckbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
}: {
  checked: boolean
  indeterminate: boolean
  onChange: () => void
  ariaLabel: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      className="size-3.5 accent-[var(--primary)]"
    />
  )
}

const PROV_TH_CLASS =
  'px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted'

/** Deep-links into the existing Stock Requests flow — same contract the till uses. */
function RequestMoveLink({
  productId,
  productName,
  sku,
}: {
  productId: string
  productName: string
  sku: string | undefined
}) {
  if (!sku) return null
  return (
    <Link
      to="/stock-requests"
      search={{ newRequest: true, productId, productName, productSku: sku }}
      className="text-xs text-primary hover:underline"
    >
      Request / move
    </Link>
  )
}

/** Statuses a unit can be sent back to its vendor from (they can all move to `rma`). */
const RETURNABLE_UNIT_STATUSES: readonly string[] = [
  'received',
  'provisioning',
  'verified',
  'sellable',
  'returned',
]

/** Overflow menu for corrections that don't belong next to the advance
 *  button — a data-entry fix, not a pipeline move. Also carries the
 *  checkout handoff for a unit that's ready to sell or rent. */
function UnitActionsMenu({
  unit,
  showCorrections,
  showCosts,
  showSell,
  showRent,
  onEdit,
  onDelete,
  onCosts,
  onReturn,
  onSell,
  onRent,
  onPrintLabel,
  onHistory,
}: {
  unit: Unit
  showCorrections: boolean
  showCosts: boolean
  showSell: boolean
  showRent: boolean
  onEdit: (unit: Unit) => void
  onDelete: (unit: Unit) => void
  onCosts: (unit: Unit) => void
  /** Only passed when staff may send this unit back to its vendor (t1154). */
  onReturn?: ((unit: Unit) => void) | undefined
  onSell: (unit: Unit) => void
  onRent: (unit: Unit) => void
  /** Only passed for a sellable unit with a barcodeable identifier (t1238). */
  onPrintLabel?: ((unit: Unit) => void) | undefined
  onHistory: (unit: Unit) => void
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`More actions for ${unit.imei}`}>
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onHistory(unit)}>
          <History /> History
        </DropdownMenuItem>
        {showSell ? (
          <DropdownMenuItem onSelect={() => onSell(unit)}>
            <ShoppingCart /> Sell this unit
          </DropdownMenuItem>
        ) : null}
        {showRent ? (
          <DropdownMenuItem onSelect={() => onRent(unit)}>
            <CalendarClock /> Rent this unit
          </DropdownMenuItem>
        ) : null}
        {onPrintLabel ? (
          <DropdownMenuItem onSelect={() => onPrintLabel(unit)}>
            <Printer /> Print price label
          </DropdownMenuItem>
        ) : null}
        {showCosts ? (
          <DropdownMenuItem onSelect={() => onCosts(unit)}>
            <DollarSign /> Purchase costs
          </DropdownMenuItem>
        ) : null}
        {onReturn ? (
          <DropdownMenuItem onSelect={() => onReturn(unit)}>
            <PackageX /> Needs to be returned
          </DropdownMenuItem>
        ) : null}
        {showCorrections ? (
          <DropdownMenuItem onSelect={() => onEdit(unit)}>
            <Pencil /> Edit IMEI
          </DropdownMenuItem>
        ) : null}
        {showCorrections ? (
          <DropdownMenuItem destructive onSelect={() => onDelete(unit)}>
            <Trash2 /> Delete record
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** A price label can go on a sellable physical unit — license keys have no
 *  sticker to put it on. */
function canPrintUnitLabel(unit: Unit): boolean {
  return unit.status === 'sellable' && unitIdentifierKind(unit.productType) !== 'license'
}

/** Bulk "Print labels" for the Sellable tab (t1238): one price label per
 *  selected unit — barcode of its IMEI/ICCID, name and price — in one print job. */
function PrintUnitLabelsDialog({
  open,
  units,
  priceFor,
  onClose,
}: {
  open: boolean
  units: Unit[]
  priceFor: (unit: Unit) => number | undefined
  onClose: () => void
}) {
  const toast = useToast()
  const [preset, setPreset] = useState<LabelPreset>('2.25x1.25')
  const printable = units.filter(canPrintUnitLabel)
  const skipped = units.length - printable.length

  const print = () => {
    const labels = []
    for (const unit of printable) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      try {
        JsBarcode(svg, unit.imei, { format: 'CODE128', displayValue: false, margin: 8 })
      } catch {
        continue
      }
      const priceCents = priceFor(unit)
      labels.push({
        barcodeSvg: svg.outerHTML,
        text: unit.imei,
        name: unit.productName,
        ...(priceCents != null ? { price: formatMoney(priceCents) } : {}),
      })
    }
    if (labels.length === 0) {
      toast.error('Nothing to print', 'None of the selected devices can be barcoded.')
      return
    }
    printLabelSheet({ labels, preset })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Print price labels</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            One label per device, each with its IMEI / ICCID barcode, product name and price.
          </p>
          <Field label="Label size">
            {(props) => (
              <Select value={preset} onValueChange={(v) => setPreset(v as LabelPreset)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(LABEL_PRESETS) as LabelPreset[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {LABEL_PRESETS[p].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {skipped > 0 ? (
            <p className="text-xs text-foreground-muted">
              {skipped} selected {skipped === 1 ? 'device is' : 'devices are'} not sellable or can't
              be barcoded and will be skipped.
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={printable.length === 0} onClick={print}>
            <Printer /> Print {printable.length} {printable.length === 1 ? 'label' : 'labels'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** What a forward stage move is about to do, held while the technician fills
 *  in the work record (t1402). */
interface PendingAdvance {
  units: Unit[]
  to: UnitStatus
}

/**
 * t1402: moving a device forward (received → provisioning → sellable) asks what
 * was done and, for phones, whether a filter went on and which kind. One answer
 * covers every unit in the batch.
 */
function AdvanceStageDialog({
  pending,
  loading,
  onClose,
  onConfirm,
}: {
  pending: PendingAdvance | null
  loading: boolean
  onClose: () => void
  onConfirm: (note: string, filter: UnitFilterAnswer | null) => void
}) {
  const [note, setNote] = useState('')
  const [installed, setInstalled] = useState<'yes' | 'no' | ''>('')
  const [kindChoice, setKindChoice] = useState('')
  const [otherKind, setOtherKind] = useState('')

  useEffect(() => {
    if (pending) {
      setNote('')
      setInstalled('')
      setKindChoice('')
      setOtherKind('')
    }
  }, [pending])

  const { data: kindsData } = useQuery({
    queryKey: queryKeys.units.filterKinds(),
    queryFn: () => api.get<{ kinds: string[] }>('/units/filter-kinds'),
    enabled: pending !== null,
  })
  const kinds = kindsData?.kinds ?? []

  const units = pending?.units ?? []
  const phones = units.filter((u) => u.productType === 'serialized')
  const asksFilter = phones.length > 0
  const kind = kindChoice === '__other' ? otherKind.trim() : kindChoice
  const filterReady = !asksFilter || installed === 'no' || (installed === 'yes' && kind !== '')
  const noteReady = note.trim().length >= 3
  const target = pending ? humanizeStatus(pending.to).toLowerCase() : ''

  return (
    <Dialog open={pending !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move {units.length} device{units.length === 1 ? '' : 's'} to {target}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {units.length > 1 ? (
            <p className="text-sm text-foreground-muted">
              Your answers are recorded on every device in this move.
            </p>
          ) : (
            <p className="text-sm text-foreground-muted">
              {units[0]?.productName} · <span className="font-identifier">{units[0]?.imei}</span>
            </p>
          )}
          <Field label="What did you do before this move?" required>
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          {asksFilter ? (
            <div className="space-y-3">
              <Field
                label="Was a filter put on?"
                required
                hint={
                  phones.length < units.length
                    ? 'Applies to the phones only; SIMs and licences just need the note.'
                    : undefined
                }
              >
                {(props) => (
                  <Select
                    value={installed || undefined}
                    onValueChange={(v) => setInstalled(v as 'yes' | 'no')}
                  >
                    <SelectTrigger {...props}>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, a filter was put on</SelectItem>
                      <SelectItem value="no">No filter</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {installed === 'yes' ? (
                <>
                  <Field label="Which kind of filter?" required>
                    {(props) => (
                      <Select value={kindChoice || undefined} onValueChange={setKindChoice}>
                        <SelectTrigger {...props}>
                          <SelectValue placeholder="Choose a filter…" />
                        </SelectTrigger>
                        <SelectContent>
                          {kinds.map((k) => (
                            <SelectItem key={k} value={k}>
                              {k}
                            </SelectItem>
                          ))}
                          <SelectItem value="__other">Other…</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  {kindChoice === '__other' ? (
                    <Field label="Filter kind" required>
                      {(props) => (
                        <Input
                          {...props}
                          maxLength={60}
                          value={otherKind}
                          onChange={(e) => setOtherKind(e.target.value)}
                        />
                      )}
                    </Field>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={!noteReady || !filterReady}
            onClick={() =>
              onConfirm(
                note.trim(),
                asksFilter
                  ? installed === 'yes'
                    ? { installed: true, kind }
                    : { installed: false }
                  : null,
              )
            }
          >
            Move to {target}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface UnitHistoryRow {
  id: string
  fromStatus: UnitStatus | null
  toStatus: UnitStatus
  actorLabel: string | null
  note: string | null
  createdAt: string
}

/** A unit's recorded moves and the work notes that went with them (t1402). */
function UnitHistoryDialog({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.units.history(unit?.id ?? ''),
    queryFn: () => api.get<UnitHistoryRow[]>(`/units/${unit?.id}/history`),
    enabled: unit !== null,
  })
  const rows = data ?? []

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>History</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            {unit?.productName} · <span className="font-identifier">{unit?.imei}</span>
          </p>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-foreground-muted">Nothing recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.id} className="rounded-md border border-border p-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {r.fromStatus && r.fromStatus !== r.toStatus
                        ? `${humanizeStatus(r.fromStatus)} → ${humanizeStatus(r.toStatus)}`
                        : humanizeStatus(r.toStatus)}
                    </span>
                    <span className="text-xs text-foreground-muted">
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {r.note ? <p className="mt-1 whitespace-pre-wrap">{r.note}</p> : null}
                  {r.actorLabel ? (
                    <p className="mt-1 text-xs text-foreground-muted">{r.actorLabel}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Corrects a mis-scanned IMEI after the device has already been received —
 *  the intake screen's per-row "×" only reaches a batch before it's submitted. */
function EditImeiDialog({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [imei, setImei] = useState('')

  useEffect(() => {
    if (unit) setImei(unit.imei)
  }, [unit])

  const kind = unitIdentifierKind(unit?.productType ?? 'serialized')
  const label = UNIT_IDENTIFIER_LABELS[kind]
  const trimmed = imei.trim()
  const unchanged = trimmed === unit?.imei
  // Client-side check for instant feedback; the server re-validates and owns
  // the "already in stock" and "sold" refusals this can't see.
  const rejectionReason = unchanged ? null : identifierRejectionReason(kind, trimmed)

  const save = useMutation({
    mutationFn: () => api.patch(`/units/${unit?.id}`, { imei: trimmed }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success(`${label} updated`)
      onClose()
    },
    onError: (error: Error) =>
      toast.error(
        `Could not update the ${label}`,
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {label}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">{unit?.productName}</p>
          <Field label={label} required error={rejectionReason ?? undefined}>
            {(props) => (
              <Input {...props} identifier value={imei} onChange={(e) => setImei(e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={unchanged || rejectionReason !== null}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Removes an erroneously received unit outright — for a device that was
 *  never really here, not one that needs RMA or scrap. */
function DeleteUnitDialog({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.delete(`/units/${unit?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Device removed')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {unit?.imei}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm">
            Removes this {unit?.productName} record entirely, including its history. Use this only
            for a device that was scanned in by mistake — a device that was really received should
            go to RMA or scrapped instead. This cannot be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One editable add-on cost row — its own local draft state, so editing one
 *  row doesn't disturb the others while its save is in flight. */
function CostRow({
  cost,
  canWrite,
  onSave,
  onDelete,
  onStatusChange,
  saving,
  deleting,
}: {
  cost: InventoryCost
  canWrite: boolean
  onSave: (patch: {
    label: string
    amountCents: number
    vendor: string | null
    invoiceRef: string | null
  }) => void
  onDelete: () => void
  onStatusChange: (status: (typeof VENDOR_PAYMENT_STATUSES)[number]) => void
  saving: boolean
  deleting: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState(cost.label)
  const [amount, setAmount] = useState((cost.amountCents / 100).toFixed(2))
  const [vendor, setVendor] = useState(cost.vendor ?? '')
  const [invoiceRef, setInvoiceRef] = useState(cost.invoiceRef ?? '')

  if (editing) {
    return (
      <tr className="border-b border-border last:border-0">
        <td className="py-2 pe-2" colSpan={5}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" />
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
            />
            <Input
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              placeholder="Vendor"
            />
            <Input
              value={invoiceRef}
              onChange={(e) => setInvoiceRef(e.target.value)}
              placeholder="Invoice ref"
            />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={!label.trim()}
              onClick={() => {
                onSave({
                  label: label.trim(),
                  amountCents: Math.round(Number(amount || 0) * 100),
                  vendor: vendor.trim() || null,
                  invoiceRef: invoiceRef.trim() || null,
                })
                setEditing(false)
              }}
            >
              Save
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-2 pe-2">
        {cost.label}
        {cost.kind === 'purchase' ? (
          <span className="ms-1.5 text-xs text-foreground-muted">(purchase)</span>
        ) : null}
        {fittedPartSummary(cost) ? (
          <span className="block text-xs text-foreground-muted">{fittedPartSummary(cost)}</span>
        ) : null}
      </td>
      <td className="py-2 pe-2 tabular">{formatMoney(cost.amountCents)}</td>
      <td className="py-2 pe-2 text-foreground-muted">{cost.vendor ?? '—'}</td>
      <td className="py-2 pe-2 text-foreground-muted">{cost.invoiceRef ?? '—'}</td>
      <td className="py-2 pe-2">
        {canWrite ? (
          <Select
            value={cost.paymentStatus}
            onValueChange={(v) => onStatusChange(v as typeof cost.paymentStatus)}
          >
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VENDOR_PAYMENT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {humanizeStatus(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge tone={VENDOR_PAYMENT_STATUS_TONE[cost.paymentStatus]}>
            {humanizeStatus(cost.paymentStatus)}
          </Badge>
        )}
      </td>
      {canWrite ? (
        <td className="py-2 ps-2 text-end">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${cost.label}`}
              onClick={() => setEditing(true)}
            >
              <Pencil />
            </Button>
            {cost.kind === 'addon' ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${cost.label}`}
                loading={deleting}
                onClick={onDelete}
              >
                <Trash2 />
              </Button>
            ) : null}
          </div>
        </td>
      ) : null}
    </tr>
  )
}

/**
 * A device's full cost picture: the base purchase cost/vendor/PO captured at
 * intake, plus any add-on costs recorded against this specific IMEI after
 * the fact (e.g. a part fitted to it) — each with its own vendor and
 * invoiced/paid status. Together these are the unit's cost basis for the
 * cost-of-goods-sold report.
 */
function UnitCostsDialog({
  unit,
  canWrite,
  onClose,
}: {
  unit: Unit | null
  canWrite: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [newLabel, setNewLabel] = useState('')
  const [newAmount, setNewAmount] = useState('')
  const [newVendor, setNewVendor] = useState('')
  const [newInvoiceRef, setNewInvoiceRef] = useState('')
  const [addMode, setAddMode] = useState<'part' | 'other'>('part')
  const [removing, setRemoving] = useState<InventoryCost | null>(null)

  const filters = { unitId: unit?.id, pageSize: 100 }
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.purchasing.costs(filters),
    queryFn: () => api.get<{ items: InventoryCost[] }>('/costs', { query: filters }),
    enabled: unit !== null,
  })
  const rows = data?.items ?? []

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })

  const addCost = useMutation({
    mutationFn: () =>
      api.post('/costs', {
        unitId: unit?.id,
        kind: 'addon',
        label: newLabel.trim(),
        amountCents: Math.round(Number(newAmount || 0) * 100),
        vendor: newVendor.trim() || null,
        invoiceRef: newInvoiceRef.trim() || null,
      }),
    onSuccess: async () => {
      await invalidate()
      setNewLabel('')
      setNewAmount('')
      setNewVendor('')
      setNewInvoiceRef('')
      toast.success('Cost added')
    },
    onError: (error: Error) =>
      toast.error('Could not add the cost', error instanceof ApiError ? error.message : undefined),
  })

  const updateCost = useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/costs/${vars.id}`, vars.patch),
    onSuccess: async () => {
      await invalidate()
      toast.success('Saved')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const deleteCost = useMutation({
    mutationFn: (id: string) => api.delete(`/costs/${id}`),
    onSuccess: async () => {
      await invalidate()
      toast.success('Cost removed')
    },
    onError: (error: Error) =>
      toast.error(
        'Could not remove the cost',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const baseCostKnown = unit?.costCents !== null && unit?.costCents !== undefined

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Purchase costs — {unit?.imei}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <div>
            <p className="mb-1.5 text-xs font-medium text-foreground-muted">Base purchase</p>
            {baseCostKnown || unit?.vendor || unit?.purchaseOrderRef ? (
              <div className="grid grid-cols-3 gap-3 rounded-md border border-border p-3 text-sm">
                <div>
                  <div className="text-xs text-foreground-muted">Cost</div>
                  <div className="tabular">
                    {baseCostKnown ? formatMoney(unit?.costCents ?? 0) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-foreground-muted">Vendor</div>
                  <div>{unit?.vendor ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-foreground-muted">Invoice / PO</div>
                  <div>{unit?.purchaseOrderRef ?? '—'}</div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-foreground-muted">
                No purchase cost was recorded when this device was received.
              </p>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-foreground-muted">
              Cost lines (purchase + add-ons)
            </p>
            {isLoading ? (
              <Skeleton className="h-16" />
            ) : rows.length === 0 ? (
              <p className="text-sm text-foreground-muted">No cost lines recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-start text-xs text-foreground-muted">
                      <th className="py-1.5 pe-2 text-start font-medium">Label</th>
                      <th className="py-1.5 pe-2 text-start font-medium">Amount</th>
                      <th className="py-1.5 pe-2 text-start font-medium">Vendor</th>
                      <th className="py-1.5 pe-2 text-start font-medium">Invoice</th>
                      <th className="py-1.5 pe-2 text-start font-medium">Status</th>
                      {canWrite ? <th className="py-1.5 ps-2" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <CostRow
                        key={row.id}
                        cost={row}
                        canWrite={canWrite}
                        saving={updateCost.isPending && updateCost.variables?.id === row.id}
                        deleting={deleteCost.isPending && deleteCost.variables === row.id}
                        onSave={(patch) => updateCost.mutate({ id: row.id, patch })}
                        onStatusChange={(paymentStatus) =>
                          updateCost.mutate({ id: row.id, patch: { paymentStatus } })
                        }
                        onDelete={() =>
                          row.componentProductId ? setRemoving(row) : deleteCost.mutate(row.id)
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {canWrite && unit ? (
            <div>
              <div className="mb-1.5 flex items-center gap-1">
                <p className="me-2 text-xs font-medium text-foreground-muted">Add a cost</p>
                <Button
                  variant={addMode === 'part' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setAddMode('part')}
                >
                  From inventory
                </Button>
                <Button
                  variant={addMode === 'other' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setAddMode('other')}
                >
                  Other cost
                </Button>
              </div>
              {addMode === 'part' ? <FitPartForm unitId={unit.id} /> : null}
              {addMode === 'other' ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="e.g. Filter X"
                  />
                  <Input
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                  <Input
                    value={newVendor}
                    onChange={(e) => setNewVendor(e.target.value)}
                    placeholder="Vendor (optional)"
                  />
                  <Input
                    value={newInvoiceRef}
                    onChange={(e) => setNewInvoiceRef(e.target.value)}
                    placeholder="Invoice ref (optional)"
                  />
                </div>
              ) : null}
              {addMode === 'other' ? (
                <div className="mt-2 flex justify-end">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={addCost.isPending}
                    disabled={!newLabel.trim() || !newAmount.trim()}
                    onClick={() => addCost.mutate()}
                  >
                    Add cost
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          <RemoveCostDialog cost={removing} onClose={() => setRemoving(null)} />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type AttributeRow = { key: string; value: string | number | boolean }

function attributeRowsToRecord(rows: AttributeRow[]): Record<string, string | number | boolean> {
  const record: Record<string, string | number | boolean> = {}
  for (const row of rows) {
    const key = row.key.trim()
    if (key) record[key] = row.value
  }
  return record
}

/**
 * Checks off what's actually true about this specific handset at its current
 * stage — e.g. a phone stocked "unfiltered" that gets filtered during
 * processing becomes "filtered" / "talk only" / "no text" here. Distinct from
 * the product's own template attributes, which this pre-seeds from (as
 * unchecked boxes) only when the device has never been checked before.
 */
function UnitAttributesDialog({
  unit,
  productAttributes,
  onClose,
}: {
  unit: Unit | null
  productAttributes: Record<string, string | number | boolean> | undefined
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [rows, setRows] = useState<AttributeRow[]>([])
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')

  useEffect(() => {
    if (!unit) return
    const existing = Object.entries(unit.attributes)
    if (existing.length > 0) {
      setRows(existing.map(([key, value]) => ({ key, value })))
    } else {
      // Nothing checked yet on this unit — start from the product's own
      // boolean attributes, unchecked, so staff tick what applies rather than
      // typing every name from scratch. Non-boolean product attributes (e.g.
      // "storage: 8GB") describe the model, not a per-unit check, so they're
      // left off the seed list.
      setRows(
        Object.entries(productAttributes ?? {})
          .filter(([, value]) => typeof value === 'boolean')
          .map(([key]) => ({ key, value: false })),
      )
    }
    setNewKey('')
    setNewValue('')
  }, [unit, productAttributes])

  const attributes = attributeRowsToRecord(rows)
  const dirty = unit ? JSON.stringify(attributes) !== JSON.stringify(unit.attributes) : false

  const { data: priceRows } = useQuery({
    queryKey: queryKeys.products.attributePrices(unit?.productId ?? ''),
    queryFn: () =>
      api.get<ProductAttributePrice[]>(`/products/${unit?.productId}/attribute-prices`),
    enabled: unit !== null,
  })
  const combos = (priceRows ?? []).map((p) => ({
    attributes: p.attributes as Record<string, string | number | boolean>,
    priceCents: p.priceCents,
  }))
  const livePriceCents = attributePriceFor(attributes, combos)

  function applyCombo(wanted: Record<string, string | number | boolean>) {
    setRows((current) => {
      const names = new Set(Object.keys(wanted).map((k) => k.trim().toLowerCase()))
      const kept = current.filter((r) => !names.has(r.key.trim().toLowerCase()))
      return [...kept, ...Object.entries(wanted).map(([key, value]) => ({ key, value }))]
    })
  }
  const trimmedNewKey = newKey.trim()
  const duplicateKey = rows.some((r) => r.key === trimmedNewKey)

  const save = useMutation({
    mutationFn: () => api.patch(`/units/${unit?.id}/attributes`, { attributes }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      toast.success('Attributes saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not save attributes',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  function updateRow(index: number, value: string | number | boolean) {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, value } : r)))
  }

  function removeRow(index: number) {
    setRows((current) => current.filter((_, i) => i !== index))
  }

  function addRow() {
    if (!trimmedNewKey || duplicateKey) return
    setRows((current) => [...current, { key: trimmedNewKey, value: newValue.trim() || true }])
    setNewKey('')
    setNewValue('')
  }

  return (
    <Dialog open={unit !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attributes — {unit?.imei}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">{unit?.productName}</p>
          {combos.length > 0 ? (
            <div className="space-y-2">
              {livePriceCents !== null ? (
                <p className="text-sm">
                  Sells for{' '}
                  <span className="tabular font-medium">{formatMoney(livePriceCents)}</span> with
                  these attributes{dirty ? ' (once saved)' : ''}.
                </p>
              ) : (
                <p className="text-sm text-foreground-muted">
                  These attributes match no attribute price, so it sells for the normal price.
                </p>
              )}
              <div className="rounded-md border border-border p-2">
                <p className="mb-1 text-xs font-medium text-foreground-muted">
                  Attribute prices for this model
                </p>
                {combos.map((combo) => (
                  <div
                    key={JSON.stringify(combo.attributes)}
                    className="flex items-center justify-between gap-2 py-0.5 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {Object.entries(combo.attributes)
                        .map(([k, v]) => (v === true ? k : `${k}: ${v}`))
                        .join(' + ')}
                    </span>
                    <span className="tabular font-medium">{formatMoney(combo.priceCents)}</span>
                    <Button variant="ghost" size="sm" onClick={() => applyCombo(combo.attributes)}>
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="space-y-1.5">
            {rows.length === 0 ? (
              <p className="text-sm text-foreground-subtle">Nothing recorded yet.</p>
            ) : (
              rows.map((row, index) => (
                <div key={row.key} className="flex items-center gap-2">
                  {typeof row.value === 'boolean' ? (
                    <label className="flex flex-1 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={row.value}
                        onChange={(e) => updateRow(index, e.target.checked)}
                      />
                      {row.key}
                    </label>
                  ) : (
                    <>
                      <span className="w-32 flex-none truncate text-sm">{row.key}</span>
                      <Input
                        className="flex-1"
                        value={row.value}
                        onChange={(e) => updateRow(index, e.target.value)}
                      />
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${row.key}`}
                    onClick={() => removeRow(index)}
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>

          <div className="flex items-end gap-2 border-t border-border pt-3">
            <Field label="Add attribute" className="flex-1">
              {(props) => (
                <Input
                  {...props}
                  placeholder="e.g. talk only"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRow()}
                />
              )}
            </Field>
            <Field label="Value (optional)" className="flex-1">
              {(props) => (
                <Input
                  {...props}
                  placeholder="blank = checked"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRow()}
                />
              )}
            </Field>
            <Button variant="secondary" disabled={!trimmedNewKey || duplicateKey} onClick={addRow}>
              Add
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The provisioning queue: a status list, not a kanban board.
 *
 * Kanban looks appealing until a shipment of two hundred phones arrives — then
 * dragging cards one at a time is the slowest possible interface. What this
 * screen actually needs is to select forty rows and advance them together, and
 * to find one device by IMEI among thousands. A table does both natively.
 */
export function ProvisioningPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { can } = useAuth()
  // inventory:adjust — the same "audit-sensitive" permission stock
  // adjustments require, since these corrections bypass the pipeline too.
  const canCorrect = can('inventory:adjust')
  // inventory:provision — the technicians' own permission, since checking off
  // a unit's attributes is a normal part of provisioning/verifying it.
  const canProvision = can('inventory:provision')
  // Purchase costs are a separate concern from provisioning corrections —
  // anyone who can see inventory can see what a device cost and whether the
  // vendor's been paid; only inventory:write can actually change it.
  const canReadCosts = can('inventory:read')
  const canWriteCosts = can('inventory:write')
  const canSell = can('orders:write')
  const canRent = can('rentals:write')

  const canPrintLabel = (unit: Unit) => canPrintUnitLabel(unit)

  const sellUnit = (unit: Unit) =>
    navigate({
      to: '/orders/new',
      search: {
        addProductId: unit.productId,
        addUnitId: unit.id,
        addUnitImei: unit.imei,
        ...(unit.locationId ? { fromLocationId: unit.locationId } : {}),
        ...(unit.locationName ? { fromLocationName: unit.locationName } : {}),
      },
    })

  const rentUnit = (unit: Unit) =>
    navigate({
      to: '/rentals',
      search: {
        newRental: true,
        unitId: unit.id,
        unitImei: unit.imei,
        productId: unit.productId,
        productName: unit.productName,
        productType: unit.productType,
        ...(unit.locationName ? { locationName: unit.locationName } : {}),
      },
    })
  const [stage, setStage] = useState<UnitStatus>('received')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null)
  const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null)
  const [attributesUnit, setAttributesUnit] = useState<Unit | null>(null)
  const [costsUnit, setCostsUnit] = useState<Unit | null>(null)
  const [returningUnit, setReturningUnit] = useState<Unit | null>(null)
  const [historyUnit, setHistoryUnit] = useState<Unit | null>(null)
  const [pendingAdvance, setPendingAdvance] = useState<PendingAdvance | null>(null)

  const { data: counts } = useQuery({
    queryKey: queryKeys.units.counts(),
    queryFn: () => api.get<Partial<Record<UnitStatus, number>>>('/units/counts'),
    refetchInterval: 30_000,
  })

  const filters = { status: stage, q: search || undefined, pageSize: 50 }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.units.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: Unit[]; total: number; page: number; pageSize: number }>('/units', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const advance = useMutation({
    mutationFn: (input: {
      unitIds: string[]
      to: UnitStatus
      note?: string | null
      filter?: UnitFilterAnswer | null
    }) =>
      api.post<{ updated: string[]; failed: Array<{ unitId: string; reason: string }> }>(
        '/units/bulk-transition',
        { note: null, filter: null, ...input },
      ),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
      setSelected(new Set())
      setPendingAdvance(null)

      if (result.failed.length === 0) {
        toast.success(
          `${result.updated.length} device${result.updated.length === 1 ? '' : 's'} moved to ${humanizeStatus(variables.to).toLowerCase()}`,
        )
      } else {
        toast.toast({
          tone: 'warning',
          title: `${result.updated.length} moved, ${result.failed.length} could not`,
          description: result.failed[0]?.reason,
        })
      }
    },
    onError: (error) =>
      toast.error(
        'Could not move those devices',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  // For the "Request / move" link on each product group — needs the SKU,
  // which the unit rows themselves don't carry. Also carries each product's
  // own `attributes` so the checklist dialog can pre-seed an unchecked unit
  // from its template rather than starting completely blank.
  const { data: skuProducts } = useQuery({
    queryKey: queryKeys.products.list({ type: 'serialized,sim,digital_license' }),
    queryFn: () =>
      api.get<{
        items: {
          id: string
          sku: string
          priceCents: number
          salePriceCents: number | null
          attributes: Record<string, string | number | boolean>
        }[]
      }>('/products', { query: { type: 'serialized,sim,digital_license', pageSize: 200 } }),
  })
  const skuByProductId = useMemo(
    () => new Map(skuProducts?.items.map((p) => [p.id, p.sku]) ?? []),
    [skuProducts],
  )
  // What the model sells for, sale price first — the same figure the
  // Products list hands the Labels page.
  const priceByProductId = useMemo(
    () => new Map(skuProducts?.items.map((p) => [p.id, p.salePriceCents ?? p.priceCents]) ?? []),
    [skuProducts],
  )
  const labelPriceCents = (unit: Unit): number | undefined =>
    unit.attributePriceCents ?? priceByProductId.get(unit.productId)

  const printUnitLabel = (unit: Unit) => {
    const priceCents = labelPriceCents(unit)
    return navigate({
      to: '/labels',
      search: {
        identifier: unit.imei,
        identifierType: unitIdentifierKind(unit.productType) === 'iccid' ? 'iccid' : 'imei',
        name: unit.productName,
        ...(priceCents != null ? { priceCents } : {}),
      },
    })
  }

  const attrsByProductId = useMemo(
    () => new Map(skuProducts?.items.map((p) => [p.id, p.attributes]) ?? []),
    [skuProducts],
  )

  const [printingLabels, setPrintingLabels] = useState(false)
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const productGroups = useMemo(() => groupUnitsByProduct(items), [items])

  function toggleExpanded(productId: string) {
    setExpandedProducts((current) => {
      const next = new Set(current)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  function toggleUnit(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleGroup(units: Unit[]) {
    const ids = units.map((u) => u.id)
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id))
    setSelected((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (allSelected) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  const allIds = items.map((u) => u.id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))
  const someSelected = !allSelected && allIds.some((id) => selected.has(id))

  function toggleAll() {
    setSelected(() => (allSelected ? new Set() : new Set(allIds)))
  }

  // Forward moves go through the work-record dialog; ids are resolved against
  // the loaded rows so the dialog knows which are phones.
  function askToAdvance(unitIds: string[], to: UnitStatus) {
    const wanted = new Set(unitIds)
    setPendingAdvance({ units: items.filter((u) => wanted.has(u.id)), to })
  }

  const nextStage = NEXT_STAGE[stage]
  const prevStage = PREV_STAGE[stage]
  const totalInPipeline = PROVISIONING_PIPELINE.reduce((sum, s) => sum + (counts?.[s] ?? 0), 0)

  return (
    <Page>
      <PageHeader
        title="Provisioning queue"
        description="Every device is filtered, then checked by a second person, before it can be sold."
      />

      {/* Funnel strip: the at-a-glance view a kanban board would give, without
          the drag-and-drop that makes bulk work slow. */}
      <div className="mb-5 flex flex-wrap gap-2">
        {PROVISIONING_PIPELINE.map((s) => {
          const count = counts?.[s] ?? 0
          const share = totalInPipeline > 0 ? (count / totalInPipeline) * 100 : 0
          return (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStage(s)
                setSelected(new Set())
              }}
              className={`min-w-36 flex-1 rounded-lg border p-3 text-start transition-colors ${
                stage === s
                  ? 'border-primary bg-primary-subtle'
                  : 'border-border bg-surface hover:border-border-strong'
              }`}
              aria-pressed={stage === s}
            >
              <p className="text-xs text-foreground-muted">{humanizeStatus(s)}</p>
              <p className="mt-0.5 text-xl font-semibold tabular">{count}</p>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${share}%` }}
                />
              </div>
              <p className="mt-2 text-2xs text-foreground-subtle">{STAGE_CAPTION[s]}</p>
            </button>
          )
        })}
      </div>

      <div className="mb-3 flex items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setSelected(new Set())
            }}
            placeholder="Find by IMEI, ICCID or serial…"
            className="ps-8"
            identifier
          />
        </div>
        <Badge tone={UNIT_STATUS_TONE[stage] ?? 'neutral'}>{humanizeStatus(stage)}</Badge>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className="w-10 px-3 py-2">
                  <TriStateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    ariaLabel={allSelected ? 'Deselect all rows' : 'Select all rows'}
                  />
                </th>
                <th className={PROV_TH_CLASS}>Product</th>
                <th className={PROV_TH_CLASS}>Identifier / Serial</th>
                <th className={PROV_TH_CLASS}>Location</th>
                <th className={PROV_TH_CLASS}>In this stage</th>
                <th className={PROV_TH_CLASS} />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }, (_, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-border last:border-0">
                    <td className="px-3 py-2.5" />
                    {Array.from({ length: 5 }, (_, columnIndex) => (
                      <td key={columnIndex} className="px-3 py-2.5">
                        <Skeleton
                          className="h-3.5"
                          style={{ width: columnIndex === 0 ? '60%' : '40%' }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : productGroups.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    {search ? (
                      <EmptyState
                        icon={Search}
                        variant="no-results"
                        title="No devices match that search"
                        description={`Nothing in ${humanizeStatus(stage).toLowerCase()} matches "${search}".`}
                        onClearFilters={() => setSearch('')}
                      />
                    ) : (
                      <EmptyState
                        icon={Boxes}
                        title={`Nothing in ${humanizeStatus(stage).toLowerCase()}`}
                        description={
                          stage === 'received'
                            ? 'Receive a shipment to start the queue.'
                            : 'Devices will appear here as they move through the chain.'
                        }
                      />
                    )}
                  </td>
                </tr>
              ) : (
                productGroups.map((group) => {
                  const single = group.units.length === 1
                  const expanded = expandedProducts.has(group.productId)
                  const groupIds = group.units.map((u) => u.id)
                  const groupAllSelected = groupIds.every((id) => selected.has(id))
                  const groupSomeSelected =
                    !groupAllSelected && groupIds.some((id) => selected.has(id))
                  const oldest = group.units.reduce((a, b) =>
                    new Date(a.statusChangedAt) < new Date(b.statusChangedAt) ? a : b,
                  )
                  const sku = skuByProductId.get(group.productId)

                  if (single) {
                    const unit = group.units[0]
                    if (!unit) return null
                    const next = NEXT_STAGE[unit.status]
                    return (
                      <tr key={group.productId} className="border-b border-border last:border-0">
                        <td className="px-3 py-2">
                          <TriStateCheckbox
                            checked={selected.has(unit.id)}
                            indeterminate={false}
                            onChange={() => toggleUnit(unit.id)}
                            ariaLabel="Select row"
                          />
                        </td>
                        <td className="px-3 py-2">{group.productName}</td>
                        <td className="px-3 py-2 font-identifier">
                          {unit.imei}
                          {unit.serialNumber ? (
                            <span className="block text-xs text-foreground-muted">
                              {unit.serialNumber}
                            </span>
                          ) : null}
                          <UnitAttributeBadges attributes={unit.attributes} />
                        </td>
                        <td className="px-3 py-2 text-foreground-muted">
                          {unit.locationName ?? '—'}
                        </td>
                        <td className="px-3 py-2">{ageBadge(unit.statusChangedAt)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-3">
                            <RequestMoveLink
                              productId={group.productId}
                              productName={group.productName}
                              sku={sku}
                            />
                            {next ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => askToAdvance([unit.id], next)}
                              >
                                {humanizeStatus(next)} <ChevronRight />
                              </Button>
                            ) : null}
                            {canProvision &&
                            (unit.status === 'provisioning' || unit.status === 'verified') ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Edit attributes for ${unit.imei}`}
                                onClick={() => setAttributesUnit(unit)}
                              >
                                <ListChecks />
                              </Button>
                            ) : null}
                            {canCorrect ||
                            canReadCosts ||
                            canPrintLabel(unit) ||
                            (canSell && unit.status === 'sellable') ||
                            (canRent && unit.status === 'sellable' && !unit.assignedStaffId) ? (
                              <UnitActionsMenu
                                unit={unit}
                                showCorrections={canCorrect && isUnitTracked(unit.productType)}
                                showCosts={canReadCosts}
                                showSell={canSell && unit.status === 'sellable'}
                                showRent={
                                  canRent && unit.status === 'sellable' && !unit.assignedStaffId
                                }
                                onEdit={setEditingUnit}
                                onDelete={setDeletingUnit}
                                onCosts={setCostsUnit}
                                onReturn={
                                  canWriteCosts && RETURNABLE_UNIT_STATUSES.includes(unit.status)
                                    ? setReturningUnit
                                    : undefined
                                }
                                onSell={sellUnit}
                                onRent={rentUnit}
                                onPrintLabel={canPrintLabel(unit) ? printUnitLabel : undefined}
                                onHistory={setHistoryUnit}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )
                  }

                  const rows = [
                    <tr key={group.productId} className="border-b border-border last:border-0">
                      <td className="px-3 py-2">
                        <TriStateCheckbox
                          checked={groupAllSelected}
                          indeterminate={groupSomeSelected}
                          onChange={() => toggleGroup(group.units)}
                          ariaLabel={`${groupAllSelected ? 'Deselect' : 'Select'} all ${group.productName} in this stage`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group.productId)}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.productName}`}
                          className="flex items-center gap-1.5 text-start font-medium hover:text-foreground"
                        >
                          {expanded ? (
                            <ChevronDown className="size-3.5 text-foreground-muted" />
                          ) : (
                            <ChevronRight className="size-3.5 text-foreground-muted" />
                          )}
                          {group.productName}
                        </button>
                      </td>
                      <td className="px-3 py-2 tabular">{group.units.length} devices</td>
                      <td className="px-3 py-2 text-foreground-muted">
                        {locationSummary(group.units)}
                      </td>
                      <td className="px-3 py-2">{ageBadge(oldest.statusChangedAt)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-3">
                          <RequestMoveLink
                            productId={group.productId}
                            productName={group.productName}
                            sku={sku}
                          />
                          {nextStage ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => askToAdvance(groupIds, nextStage)}
                            >
                              Move all <ChevronRight />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>,
                  ]

                  if (expanded) {
                    for (const unit of group.units) {
                      const next = NEXT_STAGE[unit.status]
                      rows.push(
                        <tr
                          key={unit.id}
                          className="border-b border-border bg-surface-sunken/30 last:border-0"
                        >
                          <td className="px-3 py-2">
                            <TriStateCheckbox
                              checked={selected.has(unit.id)}
                              indeterminate={false}
                              onChange={() => toggleUnit(unit.id)}
                              ariaLabel="Select row"
                            />
                          </td>
                          <td className="px-3 py-2" />
                          <td className="ps-8 py-2 font-identifier">
                            {unit.imei}
                            {unit.serialNumber ? (
                              <span className="block text-xs text-foreground-muted">
                                {unit.serialNumber}
                              </span>
                            ) : null}
                            <UnitAttributeBadges attributes={unit.attributes} />
                          </td>
                          <td className="px-3 py-2 text-foreground-muted">
                            {unit.locationName ?? '—'}
                          </td>
                          <td className="px-3 py-2">{ageBadge(unit.statusChangedAt)}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-3">
                              {next ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => askToAdvance([unit.id], next)}
                                >
                                  {humanizeStatus(next)} <ChevronRight />
                                </Button>
                              ) : null}
                              {canProvision &&
                              (unit.status === 'provisioning' || unit.status === 'verified') ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Edit attributes for ${unit.imei}`}
                                  onClick={() => setAttributesUnit(unit)}
                                >
                                  <ListChecks />
                                </Button>
                              ) : null}
                              {canCorrect ||
                              canReadCosts ||
                              canPrintLabel(unit) ||
                              (canSell && unit.status === 'sellable') ||
                              (canRent && unit.status === 'sellable' && !unit.assignedStaffId) ? (
                                <UnitActionsMenu
                                  unit={unit}
                                  showCorrections={canCorrect && isUnitTracked(unit.productType)}
                                  showCosts={canReadCosts}
                                  showSell={canSell && unit.status === 'sellable'}
                                  showRent={
                                    canRent && unit.status === 'sellable' && !unit.assignedStaffId
                                  }
                                  onEdit={setEditingUnit}
                                  onDelete={setDeletingUnit}
                                  onCosts={setCostsUnit}
                                  onReturn={
                                    canWriteCosts && RETURNABLE_UNIT_STATUSES.includes(unit.status)
                                      ? setReturningUnit
                                      : undefined
                                  }
                                  onSell={sellUnit}
                                  onRent={rentUnit}
                                  onPrintLabel={canPrintLabel(unit) ? printUnitLabel : undefined}
                                  onHistory={setHistoryUnit}
                                />
                              ) : null}
                            </div>
                          </td>
                        </tr>,
                      )
                    }
                  }

                  return rows
                })
              )}
            </tbody>
          </table>
        </div>

        <InfiniteScrollFooter
          total={total}
          loaded={items.length}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>

      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
        {stage === 'sellable' ? (
          <Button variant="secondary" size="sm" onClick={() => setPrintingLabels(true)}>
            <Printer /> Print labels
          </Button>
        ) : null}
        {nextStage ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => askToAdvance([...selected], nextStage)}
          >
            Move to {humanizeStatus(nextStage).toLowerCase()}
          </Button>
        ) : null}
        {prevStage ? (
          // A batch can need to be pulled back a step — e.g. a failed
          // verification, or a filter that needs reinstalling on devices
          // already marked ready to sell.
          <Button
            variant="secondary"
            size="sm"
            loading={advance.isPending}
            onClick={() => advance.mutate({ unitIds: [...selected], to: prevStage })}
          >
            Send back to {humanizeStatus(prevStage).toLowerCase()}
          </Button>
        ) : null}
      </BulkActionBar>

      <PrintUnitLabelsDialog
        open={printingLabels}
        units={items.filter((u) => selected.has(u.id))}
        priceFor={labelPriceCents}
        onClose={() => setPrintingLabels(false)}
      />
      <AdvanceStageDialog
        pending={pendingAdvance}
        loading={advance.isPending}
        onClose={() => setPendingAdvance(null)}
        onConfirm={(note, filter) =>
          pendingAdvance &&
          advance.mutate({
            unitIds: pendingAdvance.units.map((u) => u.id),
            to: pendingAdvance.to,
            note,
            filter,
          })
        }
      />
      <UnitHistoryDialog unit={historyUnit} onClose={() => setHistoryUnit(null)} />
      <EditImeiDialog unit={editingUnit} onClose={() => setEditingUnit(null)} />
      <DeleteUnitDialog unit={deletingUnit} onClose={() => setDeletingUnit(null)} />
      <UnitAttributesDialog
        unit={attributesUnit}
        productAttributes={
          attributesUnit ? attrsByProductId.get(attributesUnit.productId) : undefined
        }
        onClose={() => setAttributesUnit(null)}
      />
      <UnitCostsDialog
        unit={costsUnit}
        canWrite={canWriteCosts}
        onClose={() => setCostsUnit(null)}
      />
      <StartReturnDialog
        open={returningUnit !== null}
        prefill={
          returningUnit
            ? {
                unitId: returningUnit.id,
                imei: returningUnit.imei,
                productName: returningUnit.productName,
                vendorName: returningUnit.vendor,
                invoiceRef: returningUnit.purchaseOrderRef,
                amountCents: returningUnit.costCents,
              }
            : null
        }
        onClose={() => setReturningUnit(null)}
      />
    </Page>
  )
}
